/**
 * Выводы по перевесу покупок над продажами — общие для всех рядов дельты.
 *
 * «Дельта по сделкам», Spot CVD и Futures CVD меряют одно и то же, только
 * по разным рынкам. Говорить об этом они обязаны одинаково: одна и та же
 * картина не может называться у одного «поглощением», а у другого
 * «равновесием». Поэтому разбор один — `orderflow/delta-analysis.ts`, — и
 * табло собирается здесь один раз для всех.
 */

import type { Candle } from '@specter/types';
import {
  analyzeDelta,
  DELTA_WINDOW,
  type DeltaAnchor,
  type DivergenceKind,
  type SituationTone,
} from '../orderflow/delta-analysis.js';
import { signedMoney } from '../orderflow/settings.js';
import { situationRows, whenWords, type Tone } from './explain.js';
import type { IndicatorShape, ReadoutRow } from './overlays.js';

const ANCHOR_LABEL: Record<DeltaAnchor, string> = {
  day: 'С начала суток',
  week: 'С начала недели',
  all: 'За всю историю',
};

export const DIVERGENCE_TEXT: Record<DivergenceKind, string> = {
  'buyers-fading': 'покупки слабеют',
  'sellers-fading': 'продажи слабеют',
};

const TONE: Record<SituationTone, Tone> = { up: 'bull', down: 'bear', warn: 'warn', muted: 'muted' };

function signed(value: number | null): { readonly value: string; readonly tone?: 'up' | 'down' | 'muted' } {
  if (value === null) return { value: '—', tone: 'muted' };
  const side = value > 0 ? ' · покупатели' : value < 0 ? ' · продавцы' : '';
  return { value: `${signedMoney(value)}${side}`, tone: value > 0 ? 'up' : value < 0 ? 'down' : 'muted' };
}

export interface FlowExplained {
  readonly rows: readonly ReadoutRow[];
  readonly marks: readonly IndicatorShape[];
  readonly anchored: readonly (number | null)[];
  readonly delta: readonly (number | null)[];
}

export function explainFlow(
  candles: readonly Candle[],
  delta: readonly (number | null)[],
  anchor: DeltaAnchor,
): FlowExplained {
  const analysis = analyzeDelta(candles, delta, anchor);
  const marks: IndicatorShape[] = analysis.divergences.map((item) => ({
    kind: 'mark',
    time: item.time,
    price: item.price,
    tone: item.kind === 'buyers-fading' ? 'down' : 'up',
    text: DIVERGENCE_TEXT[item.kind],
  }));

  const rows: ReadoutRow[] = [];
  if (analysis.last.delta !== null || analysis.last.anchored !== null) {
    const situation = analysis.situation;
    if (situation) rows.push(...situationRows({ title: situation.title, tone: TONE[situation.tone], text: situation.text }));
    rows.push({ label: 'Эта свеча', ...signed(analysis.last.delta) });
    rows.push({ label: ANCHOR_LABEL[anchor], ...signed(analysis.last.anchored) });
    rows.push({ label: `За ${DELTA_WINDOW} свечей`, ...signed(analysis.last.window) });
    const divergence = analysis.divergences.at(-1);
    if (divergence) {
      rows.push({
        label: 'Последнее расхождение',
        value: `${whenWords(divergence.time)} · ${DIVERGENCE_TEXT[divergence.kind]}`,
        tone: divergence.kind === 'buyers-fading' ? 'down' : 'up',
      });
    }
  }
  return { rows, marks, anchored: analysis.anchored, delta: analysis.delta };
}
